import { useState, type FormEvent } from "react";

import { apiFetch, ApiError } from "@pontimed/api-client";
import { Trans, useLingui } from "@lingui/react/macro";
import { m } from "framer-motion";

import { useLegalSheet } from "@/components/legal/legal-sheet-provider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ResponsiveSheet, type ResponsiveSheetWidth } from "@/components/ui/responsive-sheet";
import { showToast } from "@/lib/ui/toast";

import type { PickerCountry } from "./use-countries-query";

export type WaitlistRole = "user" | "provider" | "clinic" | "collaborator";

interface CountryWaitlistSheetProps {
  country: PickerCountry | null;
  open: boolean;
  onClose: () => void;
}

const roleIcon: Record<WaitlistRole, string> = {
  user: "👤",
  provider: "🩺",
  clinic: "🏥",
  collaborator: "🤝",
};

/**
 * Inline waitlist signup for the country picker on apps/app. Mirrors the
 * marketing-site `WaitlistDialog` (apps/web) field-by-field but uses Lingui
 * macros instead of next-intl. Submits to `POST /api/country-waitlist`.
 *
 * `collaborator` role is offered only when `referralEnabled` is on (per
 * launch plan: collaborators earn referral commissions when the country
 * flips to `open`).
 */
export function CountryWaitlistSheet({ country, open, onClose }: CountryWaitlistSheetProps) {
  const { i18n, t } = useLingui();
  const { open: openLegal } = useLegalSheet();
  const [role, setRole] = useState<WaitlistRole>("user");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!country) return null;

  const displayName =
    country.name[i18n.locale] || country.name.en || country.name.ro || country.code;

  const visibleRoles: WaitlistRole[] = country.referralEnabled
    ? ["user", "provider", "clinic", "collaborator"]
    : ["user", "provider", "clinic"];

  const roleLabel: Record<WaitlistRole, string> = {
    user: t`Patient`,
    provider: t`Doctor`,
    clinic: t`Clinic`,
    collaborator: t`Collaborator`,
  };

  const roleDesc: Record<WaitlistRole, string> = {
    user: t`I want to book appointments and find doctors.`,
    provider: t`I'm a doctor and I want to list my practice.`,
    clinic: t`I represent a clinic or hospital.`,
    collaborator: t`I want to refer doctors and earn a commission when they subscribe.`,
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await apiFetch.post("/api/country-waitlist", {
        countryCode: country.code,
        role,
        name,
        email,
        locale: i18n.locale,
      });
      setSubmitted(true);
      showToast(t`You're on the waitlist for ${displayName}.`, "success");
      setTimeout(() => {
        setSubmitted(false);
        setEmail("");
        setName("");
        onClose();
      }, 2500);
    } catch (err) {
      if (err instanceof ApiError) {
        const body = err.body as { error?: string } | null;
        setError(body?.error || t`Something went wrong. Please try again.`);
      } else {
        setError(t`Network error. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const width: ResponsiveSheetWidth = visibleRoles.length > 3 ? "lg" : "md";

  return (
    <ResponsiveSheet
      open={open}
      onClose={onClose}
      width={width}
      title={
        submitted ? undefined : (
          <span className="flex items-center gap-3">
            <span className="text-2xl">{country.flag}</span>
            <span>{displayName}</span>
          </span>
        )
      }
      description={submitted ? undefined : <Trans>Get notified when we launch here.</Trans>}
    >
      {submitted ? (
        <m.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="flex flex-col items-center gap-3 px-6 py-10 text-center"
        >
          <div className="bg-success-soft text-success flex size-14 items-center justify-center rounded-full text-2xl">
            ✓
          </div>
          <h3 className="text-foreground text-lg font-bold">
            <Trans>You're on the list</Trans>
          </h3>
          <p className="text-muted-foreground text-sm">
            <Trans>We'll email you the moment {displayName} goes live.</Trans>
          </p>
        </m.div>
      ) : (
        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div>
            <label className="text-muted-foreground mb-2 block text-xs font-medium tracking-wider uppercase">
              <Trans>I am</Trans>
            </label>
            <div
              className={`grid gap-2 ${visibleRoles.length > 3 ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}
            >
              {visibleRoles.map((r) => {
                const isActive = role === r;
                return (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={`flex flex-col items-center gap-1.5 rounded-xl border-2 p-3 text-center transition-all ${
                      isActive
                        ? "border-primary bg-primary-soft text-foreground shadow-sm"
                        : "border-border/50 bg-card text-muted-foreground hover:border-border hover:bg-muted/30"
                    }`}
                  >
                    <span className="text-xl">{roleIcon[r]}</span>
                    <span className="text-xs font-semibold">{roleLabel[r]}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-muted-foreground mt-3 text-sm">{roleDesc[role]}</p>
          </div>

          <div>
            <label htmlFor="cwl-name" className="text-foreground mb-1 block text-xs font-medium">
              {role === "clinic" ? <Trans>Clinic name</Trans> : <Trans>Your name</Trans>}
            </label>
            <Input
              id="cwl-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={
                role === "clinic"
                  ? t`E.g. Clinica MedLife`
                  : role === "provider"
                    ? t`E.g. Dr. Maria Popescu`
                    : t`E.g. Maria Popescu`
              }
              required
              autoComplete="name"
            />
          </div>

          <div>
            <label htmlFor="cwl-email" className="text-foreground mb-1 block text-xs font-medium">
              <Trans>Email</Trans>
            </label>
            <Input
              id="cwl-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t`you@example.com`}
              required
              autoComplete="email"
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}

          {/* GDPR Art. 13 notice — public form collecting name + email. */}
          <p className="text-muted-foreground text-xs leading-relaxed">
            <Trans>
              We use your name and email only to notify you about PontiMed's launch in this country.
              You can unsubscribe at any time. Details in our{" "}
              <button
                type="button"
                onClick={() => openLegal("privacy")}
                className="text-primary underline underline-offset-2"
              >
                privacy policy
              </button>
              .
            </Trans>
          </p>

          <Button type="submit" className="mt-2 w-full" disabled={loading}>
            {loading ? <Trans>Submitting...</Trans> : <Trans>Notify me when live</Trans>}
          </Button>
        </form>
      )}
    </ResponsiveSheet>
  );
}
